import PropTypes from 'prop-types';
import React from 'react';
import { ExpandFormRow, FormRow, CloseButton, Button, Input, Checkbox, Textarea } from '@here/ref-client-ui';
import MultiplePoints from 'shared/multiplePoints';
import Searchable from '../../../views/shared/searchUtils/Searchable';
import BoundingBox from '../regionDefinition/BoundingBox';
import './avoidExclude.scss';
import utils from 'utils';

function getGeometry(geometry = []) {
  if (typeof geometry[0] === 'object') {
    return geometry.map(o => { return {lat: parseFloat(o[0] || 0), lng: parseFloat(o[1] || 0)}});
  } else // encoded
    return geometry[0];
}

function parseGeometry(geometry) {
  if (typeof geometry === 'object') {
    return geometry.map(o => {
      const { lat, lng } = o;
      if (!lat || !lng) throw new Error('lat, lng is missing!')
      return [parseFloat(o.lat || 0), parseFloat(o.lng || 0)]
    });
  } else // encoded
    return [geometry];
}

function getPostPayload(areas) {
  let areasPayload = [];
  
  // Group areas by their parent index (i)
  const groupedByParent = {};
  
  areas.forEach((area) => {
    const { i } = area;
    if (!groupedByParent[i]) {
      groupedByParent[i] = { parent: null, exceptions: [] };
    }
    
    if (!area.isException) {
      groupedByParent[i].parent = area;
    } else {
      groupedByParent[i].exceptions.push(area);
    }
  });
  
  // Build the payload with nested exceptions
  Object.keys(groupedByParent).forEach((parentIndex) => {
    const { parent, exceptions } = groupedByParent[parentIndex];
    
    if (!parent) return; // Skip if no parent
    
    const { geometry, type, radius } = parent;
    let postFeature = { name: parent.name };
    
    // Build parent feature
    if (type === "bbox") {
      postFeature.boundingBox = {
        minLat: Math.min(geometry[0][0], geometry[1][0]),
        minLon: Math.min(geometry[0][1], geometry[1][1]),
        maxLat: Math.max(geometry[0][0], geometry[1][0]),
        maxLon: Math.max(geometry[0][1], geometry[1][1])
      };
    } else if (type === "polygon" || type === "encodedPolygon") {
      postFeature.polygon = getGeometry(geometry);
    } else if (type === "corridor" || type === "encodedCorridor") {
      postFeature.corridor = {
        centerLine: getGeometry(geometry),
        radius: parseFloat(radius || 0)
      };
    }
    
    // Add exceptions by type
    if (exceptions.length > 0) {
      exceptions.forEach(exc => {
        const { geometry: excGeometry, type: excType, radius: excRadius } = exc;
        if (excType === 'corridor' || excType === 'encodedCorridor') {
          postFeature.exceptionCorridor = {
            centerLine: getGeometry(excGeometry),
            radius: parseFloat(excRadius || 0)
          };
        } else if (excType === 'polygon' || excType === 'encodedPolygon') {
          postFeature.exceptionPolygon = {
            outer: getGeometry(excGeometry)
          };
        } else if (excType === 'bbox' && excGeometry && excGeometry.length === 2) {
          postFeature.exceptionBoundingBox = {
            minLat: Math.min(excGeometry[0][0], excGeometry[1][0]),
            minLon: Math.min(excGeometry[0][1], excGeometry[1][1]),
            maxLat: Math.max(excGeometry[0][0], excGeometry[1][0]),
            maxLon: Math.max(excGeometry[0][1], excGeometry[1][1])
          };
        }
      });
    }
    
    areasPayload.push(postFeature);
  });
  
  return areasPayload;
}

function parsePostPayload(feature, isException, i) {
  const { type } = feature;
  let object;
  if (type === "boundingBox") {
    const { west = 0, south = 0, east = 0, north = 0 } = feature;
    object = {
      type: 'bbox',
      isException,
      geometry: [[north, east], [south, west]],
      i
    };
  } else if (type === "polygon" || type === "encodedPolygon") {
    const { outer } = feature;
    if (!outer) throw new Error('outer is missing!')
    object = {
      type,
      isException,
      geometry: parseGeometry(outer),
      i
    };
  } else if (type === "corridor" || type === "encodedCorridor") {
    const { polyline, radius } = feature;
    if (!polyline || !radius) throw new Error('Polyline is missing!')
    object = {
      type,
      isException,
      geometry: parseGeometry(polyline),
      radius,
      i
    };
  } else {
    throw new Error('A valid type is missing!');
  }
  
  return object;
}

class AvoidExclude extends React.Component {

  constructor(props) {
    super(props);
    this.state = {  
      isRadiusValid: true
    }

  // Local caches for displayed names, keyed by index in areas[]
  this.polygonNames = {};
  this.corridorNames = {};
  }

  onAddCorridor = () => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [], radius = 0 } = {} } } = this.props;

    setFields({
      [mode]: {
        areas: [
          ...areas,
          {
            geometry: [[], []],
            type: "corridor",
            radius,
            i: areas.length,
            name: "Corridor",
            isAutoName: true,
          },
        ],
      },
    });
  };

  onAddPolygon = () => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [] } = {} } } = this.props;

    setFields({
      [mode]: {
        areas: [
          ...areas,
          {
            geometry: [[], [], []],
            type: "polygon",
            i: areas.length,
            name: "Polygon",
            isAutoName: true,
          },
        ],
      },
    });
  };

  onAddBoundingBox = () => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [] } = {} } } = this.props;

    setFields({
      [mode]: {
        areas: [
          ...areas,
          {
            geometry: [[], []],
            type: "bbox",
            i: areas.length,
            name: "BoundingBox",
            isAutoName: true,
          },
        ],
      },
    });
  };

  onChangeAvoidArea = (index, bounds) => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields } = this.props;
    const { [mode]: { areas = [] } = {} } = fields;
    const isNameChange = typeof bounds === 'string';
    const areaUpdate = isNameChange
      ? { name: bounds, isAutoName: false }
      : { geometry: bounds };

    const result = {
      [mode]: {
        areas: areas.map((area, i) => 
          i === index ? { ...area, ...areaUpdate } : area
        ),
      },
    };

    // Preserve the other mode's areas to prevent them from being dropped
    const otherMode = mode === 'avoid' ? 'exclude' : 'avoid';
    if (fields[otherMode] && !result[otherMode]) {
      result[otherMode] = fields[otherMode];
    }

    setFields(result);
  };

  onChangeAvoidCorridorRadius = (index, e) => {

    const value = parseFloat(e.target?.value);

    this.setState({ isRadiusValid: !(isNaN(value) || value <= 0) })

    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [] } = {} } } = this.props;

    setFields({
      [mode]: {
        areas: areas.map((area, i) => 
          i === index ? { ...area, radius: e.target.value } : area
        ),
      },
    });
  }

  onChangeAvoidCorridor = (index, bounds, encoded) => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [] } = {} } } = this.props;

    const area = areas[index];
    let newBounds;
    if (encoded === undefined) {
      newBounds = bounds.map(coord => {
        if(coord.hasOwnProperty('lat')){
          if(coord.lat === '' || coord.lng ===  '') {
            return [];
          }
          return [coord.lat, coord.lng];
        }
        return coord;
      });
    } else {
      // encoded geometry: use existing geometry for naming if available
      newBounds = (area ? area.geometry  : []);
    }

    if(area){
        const { isAutoName = true, name: currentName } = area;
        const autoName =
          newBounds && newBounds.length > 0 && newBounds[0].length > 0
            ? `${newBounds[0][0]}_${newBounds[0][1]}`
            : "Corridor";
        const finalName = isAutoName ? autoName : (currentName || autoName);
        this.corridorNames[index] = finalName;

      const updateArea = encoded
        ? { geometry: bounds, name: finalName, type: "encodedCorridor", isAutoName }
        : { geometry: newBounds, type: "corridor", name: finalName, isAutoName };

      setFields({
          [mode]: {
            areas: areas.map((area, i) =>
              i === index ? { ...area, ...updateArea } : area
            ),
          },
      });
    }

 };

  onAreaNameChange = (index, e, type) => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [] } = {} } } = this.props;

    let areaName;
    if (type === 'corridor') {
      this.corridorNames[index] = e.target.value;
      areaName = this.corridorNames[index];
    } else {
      this.polygonNames[index] = e.target.value;
      areaName = this.polygonNames[index];
    }

    setFields({
      [mode]: {
        areas: areas.map((area, i) =>
          i === index ? { ...area, name: areaName, isAutoName: false } : area
        ),
      },
    });
  };

  onChangeAvoidPolygon = (index, bounds, encoded) => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields: { [mode]: { areas = [] } = {} } } = this.props;

    const area = areas[index];

    let newBounds;
    if (encoded === undefined) {
      newBounds = bounds.map(coord => {
        if (coord.hasOwnProperty('lat')) {
          if (coord.lat === '' || coord.lng === '') {
            return [];
          }
          return [coord.lat, coord.lng];
        }
        return coord;
      });
    } else {
      newBounds = area.geometry || [];
    }

    if(area){
      const { isAutoName = true, name: currentName } = area;
      const autoName =
        newBounds && newBounds.length > 0 && newBounds[0].length > 0
          ? `${newBounds[0][0]}_${newBounds[0][1]}`
          : "Polygon";
      const finalName = isAutoName ? autoName : currentName ;
      this.polygonNames[index] = finalName;

      const updateArea = encoded
        ? { geometry: bounds, type: encoded, name: finalName }
        : { geometry: newBounds, type: "polygon", name: finalName };

      setFields({
          [mode]: {
            areas: areas.map((area, i) =>
              i === index ? { ...area, ...updateArea, isAutoName } : area
            ),
          },
      });
    }
    
  };

  onRemoveAvoidArea = (index) => {
    const {
      options: { mode = 'avoid' },
      setFields,
    } = this.props;
    const { fields } = this.props;
    const { [mode]: { areas = [] } = {} } = fields;

    const { i, isException } = areas[index];
    let toBeRemovedCount = 1;
    let restAreas = [];
    areas.slice(index + 1).forEach((area, idx) => {
      if (!area.isException) {
        area.i = area.i - toBeRemovedCount;
        restAreas[idx] = area;
      } else if (area.i < index) {
        restAreas[idx] = area;
      } else if (area.i > index){
        const newI = restAreas[area.i-index-1].i;
        area.i = newI;
        restAreas[idx] = area;
      } else if (!isException && area.i === i) {
        toBeRemovedCount++;
        restAreas[idx] = null;
      } else {
        console.log("Something WRONG with this condition!")
      }
    });
    restAreas = restAreas.filter(area => area !== null);

    const result = {
      [mode]: {
        areas: [...areas.slice(0, index), ...restAreas],
      },
    };

    // Preserve the other mode's areas to prevent them from being dropped
    const otherMode = mode === 'avoid' ? 'exclude' : 'avoid';
    if (fields[otherMode] && !result[otherMode]) {
      result[otherMode] = fields[otherMode];
    }

    setFields(result);
  };

  getAvoidCorridor = () => {
    // {corridors}
  }

  getBboxInput = (area, index, length, i) => {
    const {
      options: { mode = 'avoid' }
    } = this.props;
    const uppercasedMode = mode.charAt(0).toUpperCase() + mode.slice(1);
    return (
      <div className={`rf-${mode}__area`} key={index}>
         <ExpandFormRow
          key={index}
          label={`${area.isException?'Exception':uppercasedMode} Bounding Box`}
          isExpanded={!area.isException}
          // tooltip='description'
          tooltipPlacement="right"
          className={`rf-region-definition ${index<length-2?'':'rf-without-box'}`}
        >
          <CloseButton className={`rf-${mode}-close-button`} onClick={this.onRemoveAvoidArea.bind(this, i)} />
          <BoundingBox
            type="sideBar"
            bounds={area.geometry}
            name={area.name}
            onChange={this.onChangeAvoidArea.bind(this, i)}
          />
        </ExpandFormRow>
      </div>
    )
  }

  getPolygonInput = (area, index, length, i) => {
    const {
      options: { mode = 'avoid' }
    } = this.props;
    const uppercasedMode = mode.charAt(0).toUpperCase() + mode.slice(1);
    let points = [];
    if (area.type === "polygon") {
      points = area.geometry.map(coord => {
        if(coord[0] !== '' && coord[1] !== '' )
          return {'lat': coord[0], 'lng': coord[1]}
        else 
          return {};
      });
    } else {
      points = area.geometry;
    }

    const areaIndex = i;
    

    return (
      <div className={`rf-${mode}__area`} key={index}>
        <ExpandFormRow
          key={index}
          label={`${area.isException?'Exception':uppercasedMode} Polygon`}
          isExpanded={!area.isException}
          // tooltip='description'
          tooltipPlacement="right"
          className={`rf-region-definition ${index<length-2?'':'rf-without-box'}`}
        >
        <CloseButton className={`rf-${mode}-close-button`} onClick={this.onRemoveAvoidArea.bind(this, areaIndex)} />
         <Input
          label="Name"
          value={area.name}
          onChange= { (e) => this.onAreaNameChange(areaIndex, e, 'polygon')}
          blurOnEnter
        />
        <MultiplePoints
          encoded={true}
          label="Outer"
          points={points || []}
            onChange={this.onChangeAvoidPolygon.bind(this, areaIndex)}
        />
        </ExpandFormRow>
      </div>
    )
  }

  getCorridorInput = (area, index, length, i) => {
    const {
      options: { mode = 'avoid' }
    } = this.props;
    const uppercasedMode = mode.charAt(0).toUpperCase() + mode.slice(1);
    const { radius = 0 } = area;
    let points = [];
    if (area.type === "corridor") {
      points = area.geometry.map(coord => {
        if(coord[0] !== '' && coord[1] !== '' )
          return {'lat': coord[0], 'lng': coord[1]}
        else 
          return {};
      });
    } else {
      points = area.geometry;
    }

    const areaIndex = i; 
    
    return (
      <div className={`rf-${mode}__area`} key={index}>
        <ExpandFormRow
          key={index}
          label={`${area.isException?'Exception':uppercasedMode} Corridor`}
          isExpanded={!area.isException}
          // tooltip='description'
          tooltipPlacement="right"
          className={`rf-region-definition ${index<length-2?'':'rf-without-box'}`}
        >
        <CloseButton className={`rf-${mode}-close-button`} onClick={this.onRemoveAvoidArea.bind(this, areaIndex)} />
         <Input
          label="Name"
          value={area.name}
          onChange= { (e) => this.onAreaNameChange(areaIndex, e, 'corridor')}
          blurOnEnter
        />
        <MultiplePoints
          encoded={true}
          label="Outer"
          points={points || []}
            onChange={this.onChangeAvoidCorridor.bind(this, areaIndex)}
        />
        <Input
          label="Radius *"
          value={radius}
            onChange={this.onChangeAvoidCorridorRadius.bind(this, areaIndex)}
          tooltip="Radius is a required field and must be greater than 0"
        />
        {(!this.state.isRadiusValid) && (
          <span style={{ 
            color: '#e74c3c', 
            fontSize: '12px', 
            marginTop: '5px',
            display: 'block'
          }}>
            Radius is required and must be greater than 0
          </span>
          )}
        </ExpandFormRow>
      </div>
    )
  }

  onChangeIsPost = (e) => {
    const { 
      setFields, 
      options: { mode = 'avoid' } 
    } = this.props;
    const value = utils.extractData(e);
    setFields({ [mode+'AreaIsPost']: value });
  }

  onChangePost = (e) => {
    const { 
      setFields, 
      options: { mode = 'avoid' } 
    } = this.props;
    const value = utils.extractData(e);
    
    try {
      const parsedValue = JSON.parse(value || '[]');
  
      const features = [];
      parsedValue.forEach(feature => {
        const { exceptions } = feature;
        const i = features.length;
        const t = parsePostPayload(feature, false, i);
        if (t) {
          features.push(t);
        }
  
        exceptions && exceptions.forEach(f => {
          const t = parsePostPayload(f, true, i);
          if (t) {
            features.push(t);
          }
        });
      })
      
      setFields({
        [mode]: {
          areas: features
        },
      });
    } catch(e) {

    }
    
  }
  render() {
    const {
      fields,
      options: { skipCorridor, postAble, mode = 'avoid', isPost }
    } = this.props;

    const areasMap = {
      'bbox': {},
      'polygon': {},
      'corridor': {}
    }
    const areasInputs = {
      'bbox': [],
      'polygon': [],
      'corridor': []
    }
    let areasPayload = [];
    const avoidExcludeAreaIsPost = fields[mode+'AreaIsPost'];
    const areas = (fields[mode] || {}).areas || [];

    if (!avoidExcludeAreaIsPost) {
      areas.forEach((area, index) => {
        const { i } = area;
        const { type } = areas[i];
  
        let adaptedType;
        if (type === "bbox") {
          adaptedType = 'bbox';
        } else if (type === "polygon" || type === "encodedPolygon") {
          adaptedType = 'polygon';
        } else if (type === "corridor" || type === "encodedCorridor") {
          adaptedType = 'corridor';
        }
  
        if (areasMap[adaptedType].hasOwnProperty(i)) {
          areasMap[adaptedType][i].push(area);
          areasMap[adaptedType][i].push(index);
  
        } else {
          areasMap[adaptedType][i] = [area, index];
        }
      })
  
      for (const type in areasMap){
        for (const index in areasMap[type]) {
          const areaWithExceptions = areasMap[type][index].map((area, i) => {
            if (area.type === "bbox") {
              return this.getBboxInput(area, i, areasMap[type][index].length, areasMap[type][index][i+1]);
            } else if (area.type === "polygon" || area.type === "encodedPolygon") {
              return this.getPolygonInput(area, i, areasMap[type][index].length, areasMap[type][index][i+1]);
            } else if (area.type === "corridor" || area.type === "encodedCorridor") {
              return this.getCorridorInput(area, i, areasMap[type][index].length, areasMap[type][index][i+1]);
            } else return null;
          })
          
          areasInputs[type].push((
            <div className="rf-grey-box" key={index}>
              {areaWithExceptions}
            </div>
          ))
        }
      }
    } else {
      areasPayload = getPostPayload(areas);
    }

    return (
      <div className={`rf-${mode}`}>
      {!!postAble && (
        <Checkbox
          label={`${isPost ? 'Text' : 'Is POST'}`}
          isChecked={avoidExcludeAreaIsPost || false}
          onChange={this.onChangeIsPost}
        />
      )}
      {!avoidExcludeAreaIsPost && (
        <Searchable searchKey={`${mode}[areas]`}>
          {areasInputs.bbox}
          <FormRow>
            <Button
              title={`Add ${mode} bounding box`}
              onClick={this.onAddBoundingBox}
            />
          </FormRow>
          {areasInputs.polygon}
          <FormRow>
            <Button
              title={`Add ${mode} polygon`}
              onClick={this.onAddPolygon}
            />
          </FormRow>
          {
            !skipCorridor && (
            <div>
              {areasInputs.corridor}
              <FormRow>
                <Button
                  title={`Add ${mode} corridor`}
                  onClick={this.onAddCorridor}
                />
              </FormRow>
            </div>
          )}
        </Searchable>
      )}
      {!!avoidExcludeAreaIsPost && (
        <Searchable searchKey={`${mode}[areas]`}>
            <Textarea
              value={JSON.stringify(areasPayload, null, 2)}
              onChange={this.onChangePost}
            />
        </Searchable>
      )}
      </div>
    );
  }
}

AvoidExclude.propTypes = {
  fields: PropTypes.object,
  options: PropTypes.object,
  settings: PropTypes.object,
  setFields: PropTypes.func.isRequired,
};


const extractArea = (area) => {
  if (area.type === "bbox") {
    const [topRight = [], bottomLeft = []] = area.geometry;
    return `bbox:${+topRight[1]},${+bottomLeft[0]},${+bottomLeft[1]},${+topRight[0]}`;
  } else if (area.type === "polygon") {
    const polygon = area.geometry.map(([lat, lng]) => `${+lat},${+lng}`);
    return `polygon:${polygon.join(';')}`;
  } else if (area.type === "corridor") {
    const corridor = area.geometry.map(([lat, lng]) => `${+lat},${+lng}`);
    const { radius = 0 } = area;
    return `corridor:${corridor.join(';')};r=${radius}`;
  } else if (area.type === "encodedCorridor") {
    const { radius = 0 } = area;
    return `corridor:${area.geometry[0]};r=${radius}`;
  } else {  // area.type === "encodedPolygon"
    return `polygon:${area.geometry[0]}`;
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  parseUrl: ({ parsedParams, options, postData = {} }) => {
    const { isPost, mode = 'avoid' } = options;

    if ( postData[mode] && postData[mode].areas ) {
      const features = [];
      postData[mode].areas.forEach(feature => {
        const { exceptions } = feature;
        const i = features.length;
        features.push(parsePostPayload(feature, false, i));

        exceptions && exceptions.forEach(f => {
          features.push(parsePostPayload(f, true, i));
        });
      })
      return {
        [mode]: {
          areas: features
        },
        [mode+'AreaIsPost']: true
      };
    } else if (!isPost) {
      const areasParam = parsedParams[`${mode}[areas]`] || '';
      const areasStr = areasParam.split('|').filter(Boolean);
      const areas = [];
      
      for(let avoidIndex in areasStr) {
        avoidIndex = parseInt(avoidIndex);
        const areasWExceptions = areasStr[avoidIndex].split('!exception=');
        
        for ( let idx in areasWExceptions) {
          idx = parseInt(idx);
          const isException = (idx === 0 ? false : true);
          const i = areas.length - idx;
          const [type, shape = ''] = areasWExceptions[idx].split(':');

          // bbox
          if(type === 'bbox') {
            const coords = shape.split(',');
            areas.push({
              geometry: [
                [coords[3], coords[0]],
                [coords[1], coords[2]],
              ],
              type: "bbox",
              isException,
              i
            });
          }
          // corridor
          else if (type === 'corridor') {
            const coords = shape.split(';');
            let radius = coords[coords.length - 1];
            if ((new RegExp(/^(r=)[0-9]*$/)).test(radius)) {
              coords.pop();
              radius = radius.split('=')[1] * 1;
            } else {
              radius = 0;
            }
            // corridor encoded
            if (shape.split(',').length === 1) {
              areas.push({
                geometry: [coords[0]],
                type: "encodedCorridor",
                radius,
                isException,
                i
              })
            } 
            else if (coords.length >= 1) {
              areas.push({
                geometry: coords.map(coord=>coord.split(',')),
                type: "corridor",
                radius,
                isException,
                i
              })
            } else {
              // this options should be not possible
              areas.push({
                geometry: [''],
                type: "encodedCorridor",
                radius: 0,
                isException,
                i
              })
            }
          }
          // polygon
          else {
            // polygon encoded
            if (shape.split(',').length === 1) {
              areas.push({
                geometry: [shape],
                type: "encodedPolygon",
                isException,
                i
              })
            } else if (shape.split(';').length >= 2) {
              const coords = shape.split(';');
              areas.push({
                geometry: coords.map(coord=>coord.split(',')),
                type: "polygon",
                isException,
                i
              })
            } else {
              // this options should be not possible
              areas.push({
                geometry: [''],
                type: "encodedPolygon",
                isException,
                i
              })
            }
          }
        }
      };
      delete parsedParams[`${mode}[areas]`];

      return {
        [mode]: {
          areas,
        },
      };
    } else if (postData[mode]) {
      return {
        [mode]: {
          areas: (postData[mode].areas || []).map((area, i) => {
            if (area.type === "polygon") {
              const points = area.outer.map(({lat, lng})=>[lat, lng]);
              return {
                geometry: points,
                type: "polygon",
                i
              }
            } else if ( area.type === "encodedPolygon"){
              return {
                geometry: [area.outer],
                type: "encodedPolygon",
                i
              }
            } else {  // area.type === "boundingBox"
              return {
                geometry: [
                  [area.north, area.east],
                  [area.south, area.west],
                ],
                type: "bbox",
                i
              }
            }
          }),
        },
      };
    }
    
    return {
      ...{
        [mode]: {
          areas: [],
        },
      },
    };
  },

  defaultState: (options = {}) => {
    const { mode = 'avoid' } = options;
    return {
    ...{
      [mode]: {
        areas: [],
      },
      [mode+'AreaIsPost']: false,
    },
  }},

  getRequestOptions: (fields, options) => {
    const { isPost, mode = 'avoid' } = options;
    const avoidExcludeAreaIsPost = fields[mode+'AreaIsPost']; 
    const areas = (fields[mode] || {}).areas || [];

    const hasInvalidName = areas.some(a => {
      const name = (a && a.name) ? String(a.name).trim() : '';
      return !name;
    });
    if (hasInvalidName) {
      return { validationError: 'Area name is required for all shapes.' };
    }

    if (avoidExcludeAreaIsPost) {
      const result = {
        method: 'post',
        data: {
          [mode]: {areas: getPostPayload(areas)},
        }
      };
      // Also include the other mode's areas if they exist
      const otherMode = mode === 'avoid' ? 'exclude' : 'avoid';
      if (fields[otherMode] && fields[otherMode].areas && fields[otherMode].areas.length > 0) {
        result.data[otherMode] = {areas: getPostPayload(fields[otherMode].areas)};
      }
      return result;
    } else if (!isPost) {
      const params = {};
      
      // Helper function to build area params for any mode
      const buildAreaParams = (modesAreas, modeStr) => {
        if (!modesAreas || !modesAreas.length) return null;
        let modeAreas = [];
        for (const i in modesAreas) {
          const area = modesAreas[i];
          const areaStr = extractArea(area);
          if (modeAreas.hasOwnProperty(area['i'])) {
            if (area.isException) modeAreas[area['i']].push(areaStr);
            else modeAreas[area['i']].unshift(areaStr);
          } else {
            modeAreas[area['i']] = [areaStr];
          }
        }
        modeAreas = modeAreas.filter(n => n);
        return modeAreas.length ? modeAreas.map(a => a.join('!exception=')).join('|') : null;
      };

      // Build params for both current mode and the other mode
      const currentModeParams = buildAreaParams(areas, mode);
      if (currentModeParams) params[`${mode}[areas]`] = currentModeParams;
      
      const otherMode = mode === 'avoid' ? 'exclude' : 'avoid';
      const otherAreaParams = buildAreaParams((fields[otherMode] || {}).areas, otherMode);
      if (otherAreaParams) params[`${otherMode}[areas]`] = otherAreaParams;

      return { params };
    }

    if (areas.length === 0) {
      return {};
    }
    const avoidExcludes = {};
    if (areas.length > 0) {
      avoidExcludes.areas = areas.map((area) => {
        if(area.type === "bbox") {
          const [topRight = [], bottomLeft = []] = area.geometry;
          return {
            type: 'boundingBox',
            north: (topRight[0] === undefined || topRight[0] === null || topRight[0] === '') ? null : parseFloat(topRight[0]),
            south: (bottomLeft[0] === undefined || bottomLeft[0] === null || bottomLeft[0] === '') ? null : parseFloat(bottomLeft[0]),
            east:  (topRight[1] === undefined || topRight[1] === null || topRight[1] === '') ? null : parseFloat(topRight[1]),
            west:  (bottomLeft[1] === undefined || bottomLeft[1] === null || bottomLeft[1] === '') ? null : parseFloat(bottomLeft[1]),
          };
        } else if (area.type === "polygon") {
          const geometry = area.geometry.map(([lat, lng])=>{
            return {
              lat: (lat === undefined || lat === null || lat === '') ? null : parseFloat(lat),
              lng: (lng === undefined || lng === null || lng === '') ? null : parseFloat(lng),
            }
          });
          return {
            type: 'polygon',
            outer: geometry,
          }
        } else { // area.type === "encodedPolygon"
          return {
            type: "encodedPolygon",
            outer: area.geometry[0]
          }
        }
      });
    }
    return {
      data: { [mode]: avoidExcludes },
    };
  },
  Component: AvoidExclude,
};
